import React from 'react';
import { useUser } from '../providers/UserProvider';

const cn = (...classes: (string | undefined | null | false)[]): string => {
    return classes.filter(Boolean).join(' ');
};

interface AvatarProps {
    className?: string;
    imageURL?: string;
    /** Optional pre-generated thumbnail URL. Tried first; falls back to imageURL on error. */
    thumbnailURL?: string;
    imageFile?: File | { preview: string };
    user?: { name: string; email: string };
    onClick?: () => void;
}

function getInitials(name: string | undefined | null): string {
    const safeName = (name || '').trim();
    if (!safeName) return '?';
    return safeName
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
}

export const Avatar: React.FC<AvatarProps> = ({
    className,
    imageURL,
    thumbnailURL,
    imageFile,
    user: userProp
}) => {
    const { user: loggedInUser } = useUser();
    const user = userProp || loggedInUser;

    const [imageError, setImageError] = React.useState(false);
    const [imageLoaded, setImageLoaded] = React.useState(false);
    const [srcIndex, setSrcIndex] = React.useState(0);
    const [objectUrl, setObjectUrl] = React.useState<string | null>(null);
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [computedFontSize, setComputedFontSize] = React.useState<string | undefined>(undefined);

    React.useEffect(() => {
        if (imageFile instanceof File) {
            const url = URL.createObjectURL(imageFile);
            setObjectUrl(url);
            return () => {
                URL.revokeObjectURL(url);
                setObjectUrl(null);
            };
        } else {
            setObjectUrl(null);
        }
    }, [imageFile]);

    React.useEffect(() => {
        const element = containerRef.current;
        if (!element) return;
        const update = () => {
            const rect = element.getBoundingClientRect();
            const minSide = Math.max(0, Math.min(rect.width, rect.height));
            const px = Math.floor(minSide * 0.38);
            setComputedFontSize(px > 0 ? `${px}px` : undefined);
        };
        update();
        const win = globalThis as unknown as {
            ResizeObserver?: new (callback: ResizeObserverCallback) => ResizeObserver;
            addEventListener?: (type: string, listener: () => void) => void;
            removeEventListener?: (type: string, listener: () => void) => void;
        };
        if (win.ResizeObserver) {
            const ro = new win.ResizeObserver(update);
            ro.observe(element);
            return () => ro.disconnect();
        }
        win.addEventListener?.('resize', update);
        return () => win.removeEventListener?.('resize', update);
    }, []);

    const isLoggedInUser = !userProp || userProp.email === loggedInUser?.email;
    const profilePicFallback = isLoggedInUser ? loggedInUser?.profilePictureUrl : undefined;

    const filePreview =
        imageFile && !(imageFile instanceof File)
            ? (imageFile as { preview: string }).preview
            : undefined;
    const originalSrc: string | undefined =
        (imageURL && imageURL.trim()) ||
        objectUrl ||
        filePreview ||
        profilePicFallback ||
        undefined;

    // Priority-ordered candidate URLs: thumbnail first, then original.
    const candidateSrcs = React.useMemo<string[]>(() => {
        const candidates: string[] = [];
        if (thumbnailURL && thumbnailURL.trim()) candidates.push(thumbnailURL.trim());
        if (originalSrc) candidates.push(originalSrc);
        return candidates;
    }, [thumbnailURL, originalSrc]);

    React.useEffect(() => {
        setSrcIndex(0);
        setImageLoaded(false);
        setImageError(false);
    }, [candidateSrcs]);

    const imageSrc: string | undefined = candidateSrcs[srcIndex];

    const handleImageError = React.useCallback(() => {
        const nextIndex = srcIndex + 1;
        if (nextIndex < candidateSrcs.length) {
            setSrcIndex(nextIndex);
            setImageLoaded(false);
        } else {
            setImageError(true);
        }
    }, [srcIndex, candidateSrcs]);

    const handleImageLoad = React.useCallback(() => {
        setImageLoaded(true);
        setImageError(false);
    }, []);

    // Preload and decode the avatar image for faster paint
    React.useEffect(() => {
        setImageLoaded(false);
        if (!imageSrc) return;
        let cancelled = false;
        const img = new Image();
        img.src = imageSrc;
        const onDone = () => {
            if (!cancelled) setImageLoaded(true);
        };
        const onErr = () => {
            if (!cancelled) handleImageError();
        };
        if (
            typeof (img as HTMLImageElement & { decode?: () => Promise<void> }).decode ===
            'function'
        ) {
            (img as HTMLImageElement & { decode: () => Promise<void> })
                .decode()
                .then(onDone)
                .catch(() => {
                    img.onload = onDone;
                    img.onerror = onErr;
                });
        } else {
            img.onload = onDone;
            img.onerror = onErr;
        }
        return () => {
            cancelled = true;
            img.onload = null;
            img.onerror = null;
        };
        // handleImageError changes identity when srcIndex changes — intentional, we want
        // the latest handler captured in the preload effect closure.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageSrc]);

    if (!user) {
        return (
            <div
                className={cn(
                    'relative flex shrink-0 overflow-hidden rounded-full bg-gray-300',
                    'w-full h-full aspect-square',
                    className
                )}
            >
                <div className="flex h-full w-full items-center justify-center text-gray-500">
                    ?
                </div>
            </div>
        );
    }

    const shouldShowImage = !!imageSrc && !imageError;

    return (
        <div
            ref={containerRef}
            className={cn(
                'relative flex shrink-0 overflow-hidden rounded-full text-gray-600 font-bold items-center justify-center ring-2 ring-gray-200',
                shouldShowImage ? 'bg-gray-100' : 'bg-yellow-400',
                'w-full h-full aspect-square',
                className
            )}
            style={{
                fontSize: computedFontSize,
                letterSpacing: '0.05em',
                lineHeight: '1'
            }}
        >
            {shouldShowImage ? (
                <>
                    <img
                        src={imageSrc}
                        alt={user.name || 'User avatar'}
                        className={cn(
                            'h-full w-full object-cover transition-opacity duration-200',
                            imageLoaded ? 'opacity-100' : 'opacity-0'
                        )}
                        decoding="async"
                        loading="eager"
                        onError={handleImageError}
                        onLoad={handleImageLoad}
                    />
                    {!imageLoaded && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                            <div className="h-6 w-6 rounded-full border-2 border-gray-300 border-t-gray-700 animate-spin" />
                        </div>
                    )}
                    {!imageLoaded && (
                        <div className="absolute inset-0 flex h-full w-full items-center justify-center min-w-0 overflow-hidden bg-yellow-400">
                            {getInitials(user.name)}
                        </div>
                    )}
                </>
            ) : (
                <div className="flex h-full w-full items-center justify-center min-w-0 overflow-hidden">
                    {getInitials(user.name)}
                </div>
            )}
        </div>
    );
};
